import { Formik } from 'formik';
import { isEmpty, pick } from 'lodash';
import React, { Fragment, useContext, useState, useMemo, useEffect } from 'react';
import { connect, useSelector } from 'react-redux';
import CryptoJS from 'crypto-js';
import { BookingEngineContext } from 'Views/booking/BookingEngineContext';
import { FormFeedback } from 'Components/frameworks/form-feedback/FormFeedback';
import { useAppContext } from 'Context/ReactAppContext';
import { t } from 'Src/i18n';
import type { updateAttendeesMutationType } from 'Utils/graphql-query-helper/booking/attendees/attendeesQuery';
import { selectAttendeesWithInfosByHouseholds } from 'Store/booking/attendees/attendees.selector';
import { updateAttendees as updateAttendeesAction } from 'Store/booking/proposals/proposals.action';
import type { State as userInfoType } from 'Store/user-info/reducers/userInfoReducer';
import 'Store/user-info/reducers/userInfoReducer';
import { userInfoSelector } from 'Store/user-info/userInfo.selectors';
import {
  getAddressValidationSchema,
  getAttendeeValidationSchemaByRole,
} from 'Store/validation-schema/validationSchema.selectors';
import { useTracking } from 'Tracking/tracking-api/useTracking';
import { isFeatureFlipped } from 'Utils/feature-flipping/featureFlippingUtils';
import { BE_GA_DEFAULT_PAYLOAD } from 'Views/booking/constants';
import { BE_TRACKING } from 'Tracking/constants';
import * as trackingStorageApi from 'Tracking/tracking-storage-api';
import { getPhoneCodeByCountryId, countriesToPhoneCodeList } from 'Utils/phone/phoneCode.utils';

import { OptinsContext } from 'Context/OptinsProvider';
import { compressToBase64, compressToEncodedURIComponent, decompressFromBase64 } from 'lz-string';
import {
  getFromSessionStorage,
  setInSessionStorage,
} from 'Client/session-storage-api/sessionStorageApi';
import { ContactInfoForm } from './ContactInfoForm';
import {
  findDuplicateAttendees,
  getConcernedAttendees,
  getInitialFormValues,
  handleSetErrorsForConcernedAttendees,
  renderErrorUpdatingAttendees,
} from './ContactInfoUtils';
import { CGUS_ACCEPTED_BY_API } from './constants';
type Props = {
  i18n: I18N;
  proposal: Proposal;
  connectedUserInfo: userInfoType;
  addressSchema: Array<ValidationSchema>;
  updateAttendees: (arg0: updateAttendeesMutationType) => Promise<any>;
  goToNextStep: (params?: { queryParams: Record<string, string> }) => Promise<unknown>;
  getAttendeeSchema: (isMain: boolean, isChild: boolean) => Array<Record<string, ValidationSchema>>;
};

const mapAttendeesWithPersistedProfille = (attendees) => {
  const persistedProfile = JSON.parse(decompressFromBase64(getFromSessionStorage('profile') || ''));
  return {
    ...attendees,
    adults: [{ ...attendees.adults[0], ...persistedProfile }, ...attendees.adults.slice(1)],
  };
};

const ErrorHandler = ({
  concernedAttendees,
  proposalAttendees,
  setErrors,
}: {
  concernedAttendees: Array<string>;
  proposalAttendees: ProposalAttendees;
  setErrors: any;
}) => {
  useEffect(() => {
    handleSetErrorsForConcernedAttendees({
      concernedAttendees,
      proposalAttendees,
      setErrors,
    });
  }, [concernedAttendees, proposalAttendees, setErrors]);
  return null;
};

const _ContactInfo = ({
  i18n,
  proposal,
  updateAttendees,
  connectedUserInfo,
  addressSchema,
  goToNextStep,
  getAttendeeSchema,
}: Props) => {
  const track = useTracking();
  const beEngineContext = useContext(BookingEngineContext);
  const appContext = useAppContext();
  const { isUserLogged, isUserQuickLogged } = appContext;
  const optinsContext = useContext(OptinsContext);
  const [errorMessage, setErrorMessage] = useState();
  const [concernedAttendees, setConcernedAteendees] = useState([]);
  const [hasUnselectedTravelCompanions, setHasUnselectedTravelCompanions] = useState(false);
  const { attendees } = useSelector(selectAttendeesWithInfosByHouseholds);
  const hasAddress = addressSchema.every(({ display }) => display);
  const displayParticipantsSelectInput =
    isFeatureFlipped(appContext)('booking.displayCivilityDropdown') &&
    beEngineContext?.currentStep?.name === 'participants';

  const shouldShowPhoneWithSeparatePhoneCodeField =
    isFeatureFlipped(appContext)('booking.participants.phone.showWithSeparatePhoneCodeField') ??
    false;

  // if initialValues change (the object ref change) that trigger an undesirable validation with initial values
  const initialValues = useMemo(() => {
    const values = getInitialFormValues({
      bookingProcess: i18n.bookingProcess,
      adultCivilityDefaultValue: i18n?.booking?.participants?.civilities?.adult?.[0]?.value,
      childCivilityDefaultValue: i18n?.booking?.participants?.civilities?.child?.[0]?.value,
      attendees: isUserLogged ? attendees : mapAttendeesWithPersistedProfille(attendees),
      connectedUserInfo,
      hasAddress,
      locale: appContext.locale,
      getAttendeeSchema,
      displayParticipantsSelectInput,
      preCheckedOptins: optinsContext.optins,
    });

    // Split phone number only when showWithSeparatePhoneCodeField is true
    if (shouldShowPhoneWithSeparatePhoneCodeField) {
      const splitPhoneNumber = (attendee) => {
        const mobile = attendee.mobile;
        if (!mobile || typeof mobile !== 'string') {
          return attendee;
        }

        const sortedCodes = countriesToPhoneCodeList
          .map((c) => c.phoneCode)
          .sort((a, b) => b.length - a.length);

        if (mobile.startsWith('+')) {
          const numberWithoutPlus = mobile.substring(1);

          for (const code of sortedCodes) {
            if (numberWithoutPlus.startsWith(code)) {
              return {
                ...attendee,
                mobileCode: code,
                mobile: numberWithoutPlus.substring(code.length),
              };
            }
          }
        } else if (mobile.startsWith('00')) {
          const numberWithout00 = mobile.substring(2);

          for (const code of sortedCodes) {
            if (numberWithout00.startsWith(code)) {
              return {
                ...attendee,
                mobileCode: code,
                mobile: numberWithout00.substring(code.length),
              };
            }
          }
        } else if (attendee.address?.countryCode) {
          const phoneCode = getPhoneCodeByCountryId(attendee.address.countryCode);
          if (phoneCode && mobile.startsWith(phoneCode)) {
            return {
              ...attendee,
              mobileCode: phoneCode,
              mobile: mobile.substring(phoneCode.length),
            };
          }
        }

        return attendee;
      };

      return {
        adults: values.adults.map(splitPhoneNumber),
        children: values.children.map(splitPhoneNumber),
      };
    }

    return values;
  }, [attendees]);

  const validateAttendees = ({ adults, children }) => {
    let hasErrors = false;
    const fieldsOnError = {
      firstName: true,
      lastName: true,
      birthdate: true,
    };
    const errors = {
      adults: Array(adults?.length),
      children: Array(children?.length || 0),
    };
    const adultsDuplicates = adults?.length ? findDuplicateAttendees(adults) : [];
    const childrenDuplicates = children?.length ? findDuplicateAttendees(children) : [];

    if (!isEmpty(childrenDuplicates) || !isEmpty(adultsDuplicates)) {
      hasErrors = true;
      setErrorMessage(i18n.booking.participants.labels.warningNames);
      adultsDuplicates.forEach((duplicateIndex) => {
        errors.adults[duplicateIndex] = fieldsOnError;
      });
      childrenDuplicates.forEach((duplicateIndex) => {
        errors.children[duplicateIndex] = fieldsOnError;
      });
    } else if (hasUnselectedTravelCompanions) {
      setErrorMessage(t('booking.participants.errorMessage.travelCompanionsNotSelected'));
      setHasUnselectedTravelCompanions(false);
    } else {
      setErrorMessage(undefined);
    }

    if (hasErrors) {
      return errors;
    }
  };

  const putAttendees = (
    {
      adults,
      children,
    }: {
      adults: AdultParticipants;
      children: ChildrenParticipants;
    },
    actions,
  ) => {
    actions.setSubmitting(false);
    // remove the intermediate id values to send the correct values to the API
    adults[0].optinsCGU = Object.values(adults[0].optinsCGU || {});
    // keep only those in CGUS_ACCEPTED_BY_API fix : #23104 until the API is updated to accept all CGUs
    adults[0].optinsCGU = adults[0].optinsCGU.filter((optin) =>
      CGUS_ACCEPTED_BY_API.includes((optin as any)?.channel),
    );
    // Calculate and store SHA-256 hash of email for tracking
    if (adults[0]?.email) {
      const emailSha256 = CryptoJS.SHA256(adults[0].email.toLowerCase().trim()).toString(); // eslint-disable-line new-cap

      const storedTracking = trackingStorageApi.get(BE_TRACKING);
      trackingStorageApi.set({ ...storedTracking, email_sha_256: emailSha256 });
    }

    if (shouldShowPhoneWithSeparatePhoneCodeField) {
      const formatPhoneNumber = (attendee) => {
        if (attendee.mobileCode && attendee.mobile) {
          return {
            ...attendee,
            mobile: `+${attendee.mobileCode}${attendee.mobile}`,
            mobileCode: undefined,
          };
        }
        return attendee;
      };

      adults = adults.map(formatPhoneNumber);
      children = children.map(formatPhoneNumber);
    }

    updateAttendees({
      appContext,
      proposalId: proposal.id,
      attendees: {
        adults,
        children,
      },
    })
      .then(() => {
        if (isUserLogged || isUserQuickLogged) {
          return goToNextStep();
        }

        setInSessionStorage(
          'profile',
          compressToBase64(JSON.stringify(pick(adults[0], ['firstName', 'lastName', 'address']))),
        );
        goToNextStep({
          queryParams: {
            a: compressToEncodedURIComponent(
              JSON.stringify(
                adults.map(({ firstName, lastName, birthdate }) => ({
                  firstName,
                  lastName,
                  dateOfBirth: birthdate,
                })),
              ),
            ),
          },
        });
      })
      .catch((error) => {
        setErrorMessage(renderErrorUpdatingAttendees(error));
        setConcernedAteendees(getConcernedAttendees(error) || []);
        track({
          ...BE_GA_DEFAULT_PAYLOAD,
          actionGA: 'participant',
          labelGA:
            error?.errors?.[0]?.errorCode === 'fieldCannotBeModifiedDueToUpcomingBooking'
              ? 'error_cannotMatchAttendeeNames'
              : 'error_serverError',
        });
      });
  };

  return (
    <Fragment>
      <Formik
        initialValues={initialValues}
        enableReinitialize
        onSubmit={putAttendees}
        validate={validateAttendees}
        validateOnBlur
      >
        {({ setErrors }) => {
          return (
            <Fragment>
              <ErrorHandler
                concernedAttendees={concernedAttendees}
                proposalAttendees={attendees}
                setErrors={setErrors}
              />
              <ContactInfoForm
                i18n={i18n}
                proposal={proposal}
                hasAddress={hasAddress}
                connectedUserInfo={connectedUserInfo}
                addressSchema={addressSchema}
                setErrorMessage={setErrorMessage}
                setHasUnselectedTravelCompanions={setHasUnselectedTravelCompanions}
                attendees={attendees}
              />
            </Fragment>
          );
        }}
      </Formik>

      {errorMessage && <FormFeedback hasAutoScroll isError message={errorMessage} />}
    </Fragment>
  );
};

function getConnectedUserInfo(state) {
  const userInfo = userInfoSelector(state);
  return userInfo.isQuickLoggedIn ? {} : userInfo;
}

export const ContactInfo = connect(
  (state) => ({
    connectedUserInfo: getConnectedUserInfo(state),
    addressSchema: getAddressValidationSchema(state),
    getAttendeeSchema: (isMain, isChild) =>
      getAttendeeValidationSchemaByRole(isMain, isChild)(state),
  }),
  {
    updateAttendees: updateAttendeesAction,
  },
)(_ContactInfo);
